import {
  Box,
  Flex,
  Icon,
  IconButton,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text,
  Tooltip,
  useDisclosure,
} from '@chakra-ui/react';
import { GetIngestionDocumentFragment } from '@client/graphql/__generated__/types';
import { format } from 'date-fns';
import { Eye, EyeOff, Maximize2Icon } from 'lucide-react';
import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { CompliancePDFViewer } from '~/common/pages/Compliance/CompliancePDFViewer';
import { ValidationField } from '../hooks/useAgentDocumentValidation';

interface DocumentViewerProps {
  /** Pre-resolved PDF URL; `null`/`undefined` renders the empty state. */
  pdfUrl?: string | null;
  documentName?: string;
  /** ISO timestamp the document was submitted; omitted when not yet submitted. */
  submittedAt?: string | null;
  /** Whether the document detail query is in flight. */
  loading?: boolean;
  /**
   * Every field id of the focused validation issue — one issue can span several
   * fields. All of them get the active outline in CompliancePDFViewer; the first
   * is scrolled to the center of the viewer when the selection changes. Empty
   * means no active issue.
   */
  activeFieldIds?: string[];
  /** Page → field boxes for the annotation overlay (from useAgentDocumentValidation). */
  validationFieldsMap: Map<number, ValidationField[]>;
  /** Field ids to render as (yellow) suggestion/warning highlights. */
  warningFieldIds: string[];
  /** Field ids to render as (green) passing highlights. */
  successFieldIds: string[];
  /** Per-field message for the warning highlights' callouts. */
  warningFieldMessages: Record<string, string>;
  /** Called when the embedded PDF fails to load, usually from an expired URL. */
  onPdfLoadError?: (error: Error) => void;
}

/**
 * Center pane of the agent compliance review page: a top bar with the document
 * name + submitted date, and the embedded {@link CompliancePDFViewer} showing
 * the PDF with validation annotations pinned to each classified field.
 *
 * Reuses CompliancePDFViewer wholesale for PDF rendering and annotation
 * overlays — it does not reimplement either. The highlight sets + field map are
 * derived once (client-side) by `useAgentDocumentValidation` and passed in.
 */
export const DocumentViewer: FC<DocumentViewerProps> = ({
  pdfUrl,
  documentName,
  submittedAt,
  loading = false,
  activeFieldIds = [],
  validationFieldsMap,
  warningFieldIds,
  successFieldIds,
  warningFieldMessages,
  onPdfLoadError,
}) => {
  // Scope for the scroll-to-field lookups: the Box wrapping CompliancePDFViewer.
  const pdfContainerRef = useRef<HTMLDivElement>(null);

  // Callouts start visible (deduped to one per message per page). The toggle
  // clears them entirely for an unobstructed read of the document; hovering or
  // tapping a highlight still surfaces its message either way, so hiding never
  // puts a finding out of reach.
  const [showFieldMessages, setShowFieldMessages] = useState(true);

  // The full-screen viewer. On mobile the inline PDF is constrained (it shares
  // the screen with the breadcrumb + suggestions), so the expand button opens
  // the same viewer in a full-screen modal where pinch-to-zoom / scrolling have
  // room. Useful on desktop too when the center pane is tight.
  const { isOpen, onOpen, onClose } = useDisclosure();

  // Scroll/center the PDF on the active issue's FIRST field when the selection
  // changes — mirroring the ComplianceDrawer's `activeFieldId` effect. An issue
  // spanning several pages can only be scrolled to one of them, and its first
  // field is the one the rail's page label names. CompliancePDFViewer stamps each
  // field rect with `data-preview-field-id`; we center it, falling back to its
  // page when the rect isn't mounted yet. Scoped to this viewer's container.
  const scrollTargetFieldId = activeFieldIds[0];
  useEffect(() => {
    if (!scrollTargetFieldId) {
      return;
    }

    const scope = pdfContainerRef.current;
    if (!scope) {
      return;
    }

    const frame = window.requestAnimationFrame(() => {
      const fieldElement = scope.querySelector(
        `[data-preview-field-id="${scrollTargetFieldId}"]`
      );

      if (fieldElement) {
        fieldElement.scrollIntoView({ block: 'center' });

        return;
      }

      // Fall back to the page containing the field if its rect isn't mounted.
      let pageNumber: number | undefined;
      for (const [page, fields] of validationFieldsMap.entries()) {
        if (fields.some((field) => field.id === scrollTargetFieldId)) {
          pageNumber = page;
          break;
        }
      }
      if (pageNumber != null) {
        const pageElement = scope.querySelector(
          `[data-page-number="${pageNumber}"]`
        );
        pageElement?.scrollIntoView();
      }
    });

    return () => window.cancelAnimationFrame(frame);
  }, [scrollTargetFieldId, validationFieldsMap]);

  // CompliancePDFViewer derives the PDF url from a `GetIngestionDocumentFragment`-
  // shaped `selectedDocument`, reading `contractDocuments[0].file.url` (falling
  // back to `fileUrl`). We only have a pre-resolved `pdfUrl`, so we construct
  // the minimal object the viewer reads off and cast it — the same adapter
  // pattern the SkySlope drawer uses.
  const selectedDocument = useMemo(() => {
    if (!pdfUrl) {
      return null;
    }

    return {
      documentName: documentName ?? '',
      contractDocuments: [{ file: { url: pdfUrl } }],
      fileUrl: pdfUrl,
      fields: {},
    } as unknown as GetIngestionDocumentFragment;
  }, [pdfUrl, documentName]);

  const submittedLabel = useMemo(() => {
    if (!submittedAt) {
      return null;
    }
    const parsed = new Date(submittedAt);
    if (Number.isNaN(parsed.getTime())) {
      return null;
    }

    return `Submitted ${format(parsed, 'MMM d, yyyy')}`;
  }, [submittedAt]);

  // The PDF viewer + its annotation overlays. Defined once and rendered both
  // inline and inside the full-screen modal (the same React element can be
  // placed in two positions) so the two stay in sync without duplicating props.
  const pdfViewer = (
    <CompliancePDFViewer
      activeFieldId={activeFieldIds}
      // One callout per distinct message per page, the rest revealed on
      // hover/tap: a rule that matches a field per paragraph would otherwise
      // stack its callouts into an unreadable pile over the document
      // (BLO-13495). The toggle drops even those, leaving highlights only.
      fieldMessageDisplay={showFieldMessages ? 'firstPerPage' : 'none'}
      isInternal={false}
      selectedContractDocumentId={null}
      selectedDocument={selectedDocument}
      selectedDocumentLoading={loading}
      showDocumentName={false}
      successFieldIds={successFieldIds}
      validationFieldsMap={validationFieldsMap}
      warningFieldIds={warningFieldIds}
      warningFieldMessages={warningFieldMessages}
      onPdfLoadError={onPdfLoadError}
    />
  );

  // Mirrors the extension side panel's hide toggle
  // (common/pages/Compliance/components/CollapsibleSection.tsx) down to the
  // icons and wording, so the two surfaces read as one feature. The inline and
  // full-screen headers each get a copy, but only ever ONE at a time — the
  // inline header stays in the DOM behind the open modal, and two controls with
  // the same accessible name is ambiguous for assistive tech and for queries.
  // State lives here, so the setting carries across expanding either way.
  const messageVisibilityButton = pdfUrl != null && (
    <Tooltip
      bg="compliance.tooltip.bg"
      label={
        showFieldMessages
          ? 'Hide validation messages'
          : 'Show validation messages'
      }
      variant="muted"
    >
      <IconButton
        aria-label="Show/hide validation messages"
        color="compliance.icon.primary"
        flexShrink={0}
        icon={<Icon as={showFieldMessages ? EyeOff : Eye} boxSize="18px" />}
        ml="auto"
        size="sm"
        variant="ghost"
        onClick={() => setShowFieldMessages((shown) => !shown)}
      />
    </Tooltip>
  );

  if (!loading && !pdfUrl) {
    return (
      <Flex
        align="center"
        bg="compliance.pdf.bg"
        h="full"
        justify="center"
        p={6}
        w="full"
      >
        <Text color="compliance.text.muted">
          Select a document to view it here.
        </Text>
      </Flex>
    );
  }

  return (
    <>
      <Flex bg="compliance.pdf.bg" direction="column" h="full" w="full">
        <Flex
          align="center"
          borderBottomColor="compliance.border.primary"
          borderBottomWidth={1}
          flexShrink={0}
          gap={2}
          justify="space-between"
          px={4}
          py={3}
        >
          <Box overflow="hidden">
            <Text
              color="compliance.text.primary"
              fontSize="md"
              fontWeight="semibold"
              overflow="hidden"
              textOverflow="ellipsis"
              whiteSpace="nowrap"
            >
              {documentName}
            </Text>
            {submittedLabel && (
              <Text color="compliance.text.muted" fontSize="sm">
                {submittedLabel}
              </Text>
            )}
          </Box>

          {!isOpen && messageVisibilityButton}

          {/* Expand the PDF into a full-screen modal. The document-level action
              buttons live above the suggestions in the right rail (see
              DocumentActionButtons), not here. */}
          {pdfUrl != null && (
            <IconButton
              aria-label="View document full screen"
              color="compliance.icon.primary"
              flexShrink={0}
              icon={<Icon as={Maximize2Icon} boxSize="18px" />}
              size="sm"
              variant="ghost"
              onClick={onOpen}
            />
          )}
        </Flex>

        <Box ref={pdfContainerRef} flex={1} minHeight={0} w="full">
          {/* Agent review surfaces failed-validation / suggestion fields as
              (darker) yellow warnings — NOT red errors. Passing fields show as
              light green successes. */}
          {pdfViewer}
        </Box>
      </Flex>

      <Modal
        isOpen={isOpen}
        scrollBehavior="inside"
        size="full"
        onClose={onClose}
      >
        <ModalOverlay />
        <ModalContent
          bg="compliance.background.primary"
          display="flex"
          flexDirection="column"
          m={0}
        >
          <ModalHeader
            borderBottomColor="compliance.border.primary"
            borderBottomWidth={1}
            color="compliance.text.primary"
            fontSize="md"
            pr={12}
          >
            {/* `pr` on the header leaves room for the close button; the toggle
                sits inside that row so full-screen keeps the same control. */}
            <Flex align="center" gap={2}>
              <Text
                overflow="hidden"
                textOverflow="ellipsis"
                whiteSpace="nowrap"
              >
                {documentName}
              </Text>
              {messageVisibilityButton}
            </Flex>
          </ModalHeader>
          <ModalCloseButton color="compliance.icon.primary" />
          <ModalBody
            bg="compliance.pdf.bg"
            display="flex"
            flex={1}
            minH={0}
            p={0}
          >
            {/* Mount the second viewer only while open so closing it tears down
                the extra pdf.js instance. */}
            <Box flex={1} minH={0} w="full">
              {isOpen && pdfViewer}
            </Box>
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
};
